// src/pages/leads/CustomBooking.jsx
import React, { useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Select,
  Input,
  Button,
  Checkbox,
  RadioGroup,
  Radio,
  HStack,
  VStack,
  Divider,
  useToast,
  Spinner,
} from "@chakra-ui/react";

import { HiArrowsRightLeft } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import DateInput from "../../components/PickupDate";

const cardStyle = {
  bg: "white",
  p: 6,
  rounded: "xl",
  shadow: "sm",
  border: "1px solid",
  borderColor: "#E7EAF0",
};

const labelStyle = {
  fontSize: "sm",
  fontWeight: "500",
  color: "gray.700",
  mb: 1,
};

const inputStyle = {
  h: "44px",
  bg: "#F8FAFD",
  border: "1px solid #E2E8F0",
  _focus: { borderColor: "#2E6CF6", bg: "white" },
};

const titleStyle = {
  fontSize: "18px",
  fontWeight: "700",
  color: "#2A3441",
};

const CustomBook = () => {
  const [step, setStep] = useState(1);
  const [step1, setStep1] = useState({
    pickupLocation: "",
    dropLocation: "",
    pickupDate: "",
    dropDate: "",
    pickupTime: "",
    addStops: false,
    roundTrip: false,
  });

  const [step2, setStep2] = useState({
    segmentName: "",
    description: "",
    vehicleType: "",
    luggageCapacity: "",
    passengerCapacity: "",
    fuelType: "",
    rateType: "mileage",
    ratePerKm: "",
    driverAllowance: "",
    stateTaxes: "",
    tolls: "",
    parking: "",
    nightCharges: "",
    addonCharges: "",
    comments: "",
  });

  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // VALIDATIONS SAME
  const validateStep1 = () => {
    if (!step1.pickupLocation)
      return toast({ title: "Pickup location required", status: "warning" });
    if (!step1.dropLocation)
      return toast({ title: "Drop location required", status: "warning" });
    if (!step1.pickupDate)
      return toast({ title: "Pickup date required", status: "warning" });
    return true;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    setStep(2);
  };

  return (
    <Box p={6} bg="#F3F6FD" minH="100vh">
      <Text fontSize="2xl" fontWeight="700" mb={6}>
        Leads / Add Booking /{" "}
        <span style={{ color: "#2E6CF6" }}>Custom Booking</span>
      </Text>

      {/* TOP PICK / DROP SECTION */}
      <Grid templateColumns={{ base: "1fr", md: "1fr 70px 1fr" }} gap={6}>
        {/* PICK UP CARD */}
        <Box {...cardStyle}>
          <Flex justify="space-between" mb={4}>
            <Text fontWeight="600">Pick - Up</Text>
            <Text fontSize="sm" color="gray.500">
              Select your city
            </Text>
          </Flex>

          <Grid templateColumns="1fr 1fr" gap={4}>
            <Box>
              <Text {...labelStyle}>Locations</Text>
              <Select
                {...inputStyle}
                placeholder="Select City"
                value={step1.pickupLocation}
                onChange={(e) =>
                  setStep1((s) => ({ ...s, pickupLocation: e.target.value }))
                }
              >
                <option>Mumbai</option>
                <option>Delhi</option>
                <option>Bangalore</option>
                <option>Hyderabad</option>
              </Select>
            </Box>

            <Box>
              <Text {...labelStyle}>Date</Text>
              <DateInput
                name="pickupDate"
                value={step1.pickupDate}
                handleInputChange={(e) =>
                  setStep1((s) => ({ ...s, pickupDate: e.target.value }))
                }
              />
            </Box>
          </Grid>

          <HStack mt={4}>
            <Checkbox
              isChecked={step1.addStops}
              onChange={(e) =>
                setStep1((s) => ({ ...s, addStops: e.target.checked }))
              }
            >
              Add Stops
            </Checkbox>

            <Checkbox
              isChecked={step1.roundTrip}
              onChange={(e) =>
                setStep1((s) => ({ ...s, roundTrip: e.target.checked }))
              }
            >
              Round Trip
            </Checkbox>
          </HStack>
        </Box>

        {/* MIDDLE ICON */}
        <Flex justify="center" align="center">
          <Box
            bg="white"
            p={4}
            rounded="full"
            shadow="sm"
            border="1px solid #E2E8F0"
          >
            <HiArrowsRightLeft size={28} color="#2E6CF6" />
          </Box>
        </Flex>

        {/* DROP OFF CARD */}
        <Box {...cardStyle}>
          <Flex justify="space-between" mb={4}>
            <Text fontWeight="600">Drop - Off</Text>
            <Text fontSize="sm" color="gray.500">
              Select your city
            </Text>
          </Flex>

          <Grid templateColumns="1fr 1fr" gap={4}>
            <Box>
              <Text {...labelStyle}>Locations</Text>
              <Select
                {...inputStyle}
                placeholder="Select City"
                value={step1.dropLocation}
                onChange={(e) =>
                  setStep1((s) => ({ ...s, dropLocation: e.target.value }))
                }
              >
                <option>Pune</option>
                <option>Chennai</option>
                <option>Tirupati</option>
              </Select>
            </Box>

            <Box>
              <Text {...labelStyle}>Date</Text>
              <DateInput
                name="dropDate"
                value={step1.dropDate}
                handleInputChange={(e) =>
                  setStep1((s) => ({ ...s, dropDate: e.target.value }))
                }
              />
            </Box>
          </Grid>
        </Box>
      </Grid>

      {/* CREATE CUSTOM BOOKING CARD */}
      <Box {...cardStyle} mt={6}>
        <Flex justify="space-between" mb={4}>
          <Heading size="md">Create Custom Booking</Heading>
          <Text color="gray.500">Step 1 of 2</Text>
        </Flex>

        <Divider mb={4} />

        <Grid templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }} gap={4}>
          {/* Segment */}
          <Box>
            <Text {...labelStyle}>Segment Name</Text>
            <Select
              {...inputStyle}
              placeholder="Select Segment"
              value={step2.segmentName}
              onChange={(e) =>
                setStep2((s) => ({ ...s, segmentName: e.target.value }))
              }
            >
              <option>Standard</option>
              <option>Premium</option>
            </Select>
          </Box>

          {/* Description */}
          <Box gridColumn={{ md: "span 3" }}>
            <Text {...labelStyle}>Description</Text>
            <Input
              {...inputStyle}
              placeholder="Enter Model"
              value={step2.description}
              onChange={(e) =>
                setStep2((s) => ({ ...s, description: e.target.value }))
              }
            />
          </Box>

          {/* Vehicle Type */}
          <Box>
            <Text {...labelStyle}>Vehicle Type</Text>
            <Select
              {...inputStyle}
              placeholder="Select Vehicle"
              value={step2.vehicleType}
              onChange={(e) =>
                setStep2((s) => ({ ...s, vehicleType: e.target.value }))
              }
            >
              <option>Dzire / Etios</option>
              <option>Innova</option>
              <option>SUV</option>
            </Select>
          </Box>

          {/* Luggage */}
          <Box>
            <Text {...labelStyle}>Luggage Capacity</Text>
            <Input
              {...inputStyle}
              placeholder="Enter Luggage"
              value={step2.luggageCapacity}
              onChange={(e) =>
                setStep2((s) => ({ ...s, luggageCapacity: e.target.value }))
              }
            />
          </Box>

          {/* Passenger */}
          <Box>
            <Text {...labelStyle}>Passenger Capacity</Text>
            <Input
              {...inputStyle}
              placeholder="Enter Seating"
              value={step2.passengerCapacity}
              onChange={(e) =>
                setStep2((s) => ({ ...s, passengerCapacity: e.target.value }))
              }
            />
          </Box>

          {/* Fuel */}
          <Box>
            <Text {...labelStyle}>Fuel Type</Text>
            <Select
              {...inputStyle}
              placeholder="Select Fuel"
              value={step2.fuelType}
              onChange={(e) =>
                setStep2((s) => ({ ...s, fuelType: e.target.value }))
              }
            >
              <option>Petrol</option>
              <option>Diesel</option>
              <option>CNG</option>
            </Select>
          </Box>
        </Grid>
      </Box>

      {/* PACKAGE TYPE CARD */}
      <Box {...cardStyle} mt={6} mb={10}>
        <Flex justify="space-between" mb={4}>
          <Heading size="md">Package Type</Heading>
          <Text color="gray.500">Step 2 of 2</Text>
        </Flex>

        <Divider mb={4} />

        {/* Rate Type */}
        <RadioGroup
          onChange={(val) => setStep2((s) => ({ ...s, rateType: val }))}
          value={step2.rateType}
        >
          <HStack spacing={6} mb={4}>
            <Radio value="fixed">Fixed Rate</Radio>
            <Radio value="mileage">Mileage Basis</Radio>
          </HStack>
        </RadioGroup>

        <Grid templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }} gap={4}>
          {[
            ["Rate (₹/KM)", "ratePerKm"],
            ["Driver Allowance (₹/Day)", "driverAllowance"],
            ["State Taxes (₹)", "stateTaxes"],
            ["Tolls (₹)", "tolls"],
            ["Parking", "parking"],
            ["Night Charges", "nightCharges"],
            ["Addon Charges", "addonCharges"],
          ].map(([label, key]) => (
            <Box key={key}>
              <Text {...labelStyle}>{label}</Text>
              <Input
                {...inputStyle}
                placeholder={label}
                value={step2[key]}
                onChange={(e) =>
                  setStep2((s) => ({ ...s, [key]: e.target.value }))
                }
              />
            </Box>
          ))}

          {/* Comments full width */}
          <Box gridColumn={{ md: "span 4" }}>
            <Text {...labelStyle}>Comments</Text>
            <Input
              {...inputStyle}
              placeholder="Enter Comments"
              value={step2.comments}
              onChange={(e) =>
                setStep2((s) => ({ ...s, comments: e.target.value }))
              }
            />
          </Box>
        </Grid>

        <Flex justify="flex-end" gap={4} mt={8}>
          <Button variant="outline" borderRadius="lg" px={6}>
            Cancel
          </Button>

          <Button
            colorScheme="blue"
            px={8}
            size="md"
            rounded="lg"
            onClick={handleNext}
          >
            Next
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default CustomBook;
